import Upload from "@/models/Upload";
import ByteEncoder from "@/util/byteEncoder";
import FileAES from "@/util/fileAES";
import Base64Encoder from "@/util/base64encoder";
import store from '@/store';
import AccountDataAES from "@/util/accountDataAES";
import KeyUtility from "@/util/keyUtility";

export default class Letterbox {
    public id!: string;
    public name!: string;
    public message!: string;
    public iv!: string;
    public passwordProtected!: boolean;
    public uploadKey!: string;
    public uploads!: Upload[];
    public secretEncrypted!: string;
    public secretEncryptedIv!: string;
    public created!: number;

    public static from(data: any): Letterbox {
        const letterbox = new Letterbox();
        letterbox.id = data.id;
        letterbox.uploadKey = data.uploadKey;
        letterbox.iv = data.iv;
        letterbox.name = data.name;
        letterbox.message = data.message;
        letterbox.passwordProtected = data.passwordProtected;
        letterbox.uploads = data.uploads;
        return letterbox;
    }

    public async getEncrypted(key: CryptoKey, iv: Uint8Array): Promise<Letterbox> {
        const letterbox = new Letterbox();
        const aes: FileAES = new FileAES(key);

        letterbox.id = this.id;
        letterbox.uploadKey = this.uploadKey;
        letterbox.passwordProtected = this.passwordProtected;
        letterbox.uploads = this.uploads;
        letterbox.iv = Base64Encoder.bytesToString(iv);

        if (store.getters.isLoggedIn && store.state.account.accountData) {
            //Encrypt link with account key:
            const accountEncryption: AccountDataAES = await AccountDataAES.fromAccountData(store.state.account.masterKey, store.state.account.accountData);
            const secretIv = AccountDataAES.generateIV();
            const letterboxKeyBase64 = await KeyUtility.exportKeyToBase64(key);
            letterbox.secretEncrypted = await accountEncryption.encryptStringAndConvertToBase64(letterboxKeyBase64, secretIv);
            letterbox.secretEncryptedIv = Base64Encoder.bytesToString(secretIv);
        }

        if (this.name) {
            const encryptedName: ArrayBuffer = await aes.encryptChunk(ByteEncoder.stringToBytes(this.name), iv);
            letterbox.name = Base64Encoder.bytesToString(new Uint8Array(encryptedName));
        }

        if (this.message) {
            const encryptedMessage: ArrayBuffer = await aes.encryptChunk(ByteEncoder.stringToBytes(this.message), iv);
            letterbox.message = Base64Encoder.bytesToString(new Uint8Array(encryptedMessage));
        }

        return letterbox;
    }

    public async getDecrypted(key: CryptoKey): Promise<Letterbox> {
        const letterbox = new Letterbox();
        const aes: FileAES = new FileAES(key);

        letterbox.id = this.id;
        letterbox.uploadKey = this.uploadKey;
        letterbox.passwordProtected = this.passwordProtected;
        letterbox.uploads = this.uploads;
        //Do not encrypt these two, we do this only in the user backend, this has to be done with the key of the user
        letterbox.secretEncrypted = this.secretEncrypted;
        letterbox.secretEncryptedIv = this.secretEncryptedIv;

        const iv: Uint8Array = Base64Encoder.stringToBytes(this.iv);

        if (this.name) {
            const decryptedName: ArrayBuffer = await aes.decryptChunk(Base64Encoder.stringToBytes(this.name), iv);
            letterbox.name = ByteEncoder.bytesToString(new Uint8Array(decryptedName));
        }

        if (this.message) {
            const decryptedMessage: ArrayBuffer = await aes.decryptChunk(Base64Encoder.stringToBytes(this.message), iv);
            letterbox.message = ByteEncoder.bytesToString(new Uint8Array(decryptedMessage));
        }

        return letterbox;
    }
}
